import { useMemo, useState } from "react";

const typeOrder = {
  Wareneingang: 1,
  Retoure: 2,
  "Postruecklaeufer": 3,
  "PostrÃ¼cklÃ¤ufer": 3,
  Warenausgang: 4,
  "BIO WE": 5,
  Mehraufwand: 6,
};

const emptyManualLine = {
  nr: "",
  description: "",
  quantity: "",
  unit: "",
  unitPrice: "",
};

function parseRecordDate(record) {
  const rawValue = record?.timestamp || "";
  const match = rawValue.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:,\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;
  const [, day, month, year, hour = "0", minute = "0", second = "0"] = match;
  return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getMonthStart(monthValue) {
  return monthValue ? `${monthValue}-01` : "";
}

function getMonthEnd(monthValue) {
  if (!monthValue) return "";
  const [year, month] = monthValue.split("-").map(Number);
  const end = new Date(year, month, 0);
  return `${year}-${String(month).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
}

function shouldShowInMonth(item, dateFrom, dateTo) {
  const recordDate = parseRecordDate(item);
  if (!recordDate || !dateFrom || !dateTo) return false;
  const startDate = new Date(`${dateFrom}T00:00:00`);
  const endDate = new Date(`${dateTo}T23:59:59`);
  return (recordDate >= startDate && recordDate <= endDate) || (!item.processed && recordDate < startDate);
}

function getFile(supplier, label) {
  return (supplier?.contractFiles || []).find((file) =>
    String(file.label || "").toLowerCase().includes(label)
  );
}

function money(value) {
  return `${(Number(value) || 0).toFixed(2)} EUR`;
}

function lineTotal(line) {
  return (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
}

function printInvoice(invoice) {
  const popup = window.open("", "_blank", "width=900,height=700");
  if (!popup) return;
  popup.document.write(`
    <html><head><title>Rechnung ${invoice.customerName}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;color:#111827}
      h1,h2{margin:0 0 12px}
      table{width:100%;border-collapse:collapse;margin:14px 0}
      th,td{border:1px solid #d1d5db;padding:8px;text-align:left}
      th{background:#f3f4f6}
      .muted{color:#6b7280}
    </style></head><body>
    <h1>Rechnungsaufstellung</h1>
    <p><strong>${invoice.customerName}</strong> ${invoice.customerNumber ? `- JTL ${invoice.customerNumber}` : ""}</p>
    <p class="muted">Zeitraum: ${invoice.month || ""}</p>
    ${invoice.html || ""}
    </body></html>
  `);
  popup.document.close();
  popup.focus();
  popup.print();
}

export default function Abrechnung({
  records = [],
  outgoingRecords = [],
  bioRecords = [],
  extraEfforts = [],
  suppliers = [],
  invoices = [],
  onSaveInvoice,
  onSaveAgreement,
}) {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);
  const [openPanel, setOpenPanel] = useState(null);
  const [agreements, setAgreements] = useState({});
  const [manualLines, setManualLines] = useState({});
  const dateFrom = getMonthStart(selectedMonth);
  const dateTo = getMonthEnd(selectedMonth);

  const supplierByName = useMemo(
    () => new Map(suppliers.map((supplier) => [supplier.name, supplier])),
    [suppliers]
  );

  const groupedCustomers = useMemo(() => {
    const rows = [
      ...records.filter((record) => shouldShowInMonth(record, dateFrom, dateTo)).map((record) => ({
        source: "Wareneingang",
        type: record.type,
        customer: record.selectedSupplier,
        text: `${record.timestamp} | ${record.packageCount || 0} Pakete | ${record.palletCount || 0} Paletten | Schaden: ${record.damagedPackages || 0} Pakete / ${record.damagedPallets || 0} Paletten | ${record.comment || ""}`,
      })),
      ...outgoingRecords.filter((record) => shouldShowInMonth(record, dateFrom, dateTo)).map((record) => ({
        source: "Warenausgang",
        type: "Warenausgang",
        customer: record.selectedSupplier,
        text: `${record.timestamp} | ${record.packageCount || 0} Pakete | ${record.palletCount || 0} Paletten | ${record.comment || ""}`,
      })),
      ...bioRecords.filter((record) => shouldShowInMonth(record, dateFrom, dateTo)).map((record) => ({
        source: "Wareneingang",
        type: "BIO WE",
        customer: record.selectedSupplier,
        text: `${record.timestamp} | BIO WE | ${record.packageCount || 0} Pakete | ${record.palletCount || 0} Paletten`,
      })),
      ...extraEfforts.filter((effort) => shouldShowInMonth(effort, dateFrom, dateTo)).map((effort) => ({
        source: "Mehraufwand",
        type: "Mehraufwand",
        customer: effort.customer,
        text: `${effort.timestamp} | ${effort.effortType} ${effort.expenseType || ""} | Schaden: ${effort.damagedPackages || 0} Pakete / ${effort.damagedPallets || 0} Paletten | ${effort.durationMinutes || ""} Min. | ${money(effort.expenseAmount)}`,
      })),
    ].sort((left, right) => (typeOrder[left.type] || 99) - (typeOrder[right.type] || 99));

    const grouped = new Map();
    rows.forEach((row) => {
      const customer = row.customer || "Ohne Kunde";
      if (!grouped.has(customer)) grouped.set(customer, []);
      grouped.get(customer).push(row);
    });

    return Array.from(grouped.entries()).map(([customer, customerRows]) => ({
      customer,
      supplier: supplierByName.get(customer),
      rows: customerRows,
    })).sort((left, right) => left.customer.localeCompare(right.customer));
  }, [bioRecords, dateFrom, dateTo, extraEfforts, outgoingRecords, records, supplierByName]);

  const getExistingInvoice = (customer) =>
    invoices.find((invoice) => invoice.customerName === customer && invoice.month === selectedMonth);
  const getLines = (customer) =>
    manualLines[customer] ||
    getExistingInvoice(customer)?.data?.sections?.freitext ||
    [{ ...emptyManualLine }];
  const setLine = (customer, index, key, value) => {
    setManualLines((current) => ({
      ...current,
      [customer]: getLines(customer).map((line, lineIndex) =>
        lineIndex === index ? { ...line, [key]: value } : line
      ),
    }));
  };
  const removeLine = (customer, index) => {
    setManualLines((current) => {
      const nextLines = getLines(customer).filter((line, lineIndex) => lineIndex !== index);
      return { ...current, [customer]: nextLines.length ? nextLines : [{ ...emptyManualLine }] };
    });
  };

  const createInvoicePayload = (group, status) => {
    const lines = getLines(group.customer);
    const supplier = group.supplier || {};
    const sections = {
      wareneingang: group.rows.filter((row) => row.source === "Wareneingang"),
      warenausgang: group.rows.filter((row) => row.source === "Warenausgang"),
      kartonagen: supplier.cartonPrices || [],
      porto: supplier.shippingPrices || {},
      mehraufwand: group.rows.filter((row) => row.source === "Mehraufwand"),
      lagerplatz: [],
      freitext: lines,
    };
    const html = document.getElementById(`preview-${group.customer}`)?.innerHTML || "";
    return {
      customerId: supplier.id || "",
      customerName: group.customer,
      customerNumber: supplier.customerNumber || "",
      month: selectedMonth,
      status,
      data: { sections, packageType: supplier.packageType || "", billingPeriod: supplier.billingPeriod || "", html },
    };
  };

  const saveInvoice = async (group, status = "saved") => {
    const existing = getExistingInvoice(group.customer);
    const payload = { ...createInvoicePayload(group, status), id: existing?.id || "" };
    const saved = await onSaveInvoice?.(payload);
    if (status === "printed") {
      printInvoice({ ...payload, id: saved?.id, html: payload.data.html });
    }
  };

  return (
    <div className="page-stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">Abrechnung</h1>
      </div>

      <section className="card">
        <div className="form-grid columns-3">
          <label className="field"><span>Monat</span><input type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} /></label>
          <label className="field"><span>Monatsanfang</span><input type="date" value={dateFrom} readOnly /></label>
          <label className="field"><span>Monatsende</span><input type="date" value={dateTo} readOnly /></label>
        </div>
      </section>

      {groupedCustomers.map((group) => {
        const supplier = group.supplier || {};
        const packagePrice = supplier.packagePrices?.[supplier.packageType] || "";
        const contract = getFile(supplier, "vertrag");
        const offer = getFile(supplier, "angebot") || getFile(supplier, "anlage");
        const sepa = getFile(supplier, "sepa");
        const panelKey = `${group.customer}-${selectedMonth}`;
        return (
          <section className="card" key={group.customer}>
            <div className="billing-customer-head">
              <div>
                <h2>{group.customer}</h2>
                <p className="section-text">JTL Kundennummer {supplier.customerNumber || "-"}</p>
              </div>
              <button type="button" className="billing-icon billing-icon-alert" title="Sonstige Vereinbarungen" onClick={() => setOpenPanel(`${panelKey}-agreement`)}>!</button>
              <button type="button" className="billing-icon" title="Vertrag" onClick={() => setOpenPanel(`${panelKey}-contract`)}>Liste</button>
              <button type="button" className="billing-icon" title="Angebot" onClick={() => setOpenPanel(`${panelKey}-offer`)}>Liste</button>
              <button type="button" className="billing-icon" title="Sepa Mandat" onClick={() => setOpenPanel(`${panelKey}-sepa`)}>Liste</button>
            </div>
            <div className="billing-meta-row">
              <span>{supplier.isPackageCustomer ? `${supplier.packageType || "Paketkunde"} ${packagePrice ? `- ${money(packagePrice)}` : ""}` : "Individueller Kunde"}</span>
              <span>{supplier.billingPeriod || "monatlich"}</span>
              <span>Ueberhang Paketkunden: folgt</span>
              <button type="button" className="billing-icon billing-icon-wide" onClick={() => setOpenPanel(`${panelKey}-emails`)}>Rechnungsempfaenger</button>
            </div>

            {openPanel === `${panelKey}-agreement` && (
              <div className="billing-popup">
                <button type="button" className="modal-close" onClick={() => setOpenPanel(null)}>x</button>
                <h3>Sonstige Vereinbarungen</h3>
                <textarea value={agreements[group.customer] ?? supplier.billingAgreement ?? ""} onChange={(event) => setAgreements({ ...agreements, [group.customer]: event.target.value })} />
                <button type="button" className="button button-primary" onClick={() => onSaveAgreement?.(supplier.id, agreements[group.customer] ?? supplier.billingAgreement ?? "")}>Speichern</button>
              </div>
            )}
            {openPanel === `${panelKey}-contract` && <FilePopup file={contract} onClose={() => setOpenPanel(null)} />}
            {openPanel === `${panelKey}-offer` && <FilePopup file={offer} onClose={() => setOpenPanel(null)} />}
            {openPanel === `${panelKey}-sepa` && <FilePopup file={sepa} onClose={() => setOpenPanel(null)} />}
            {openPanel === `${panelKey}-emails` && (
              <div className="billing-popup">
                <button type="button" className="modal-close" onClick={() => setOpenPanel(null)}>x</button>
                <h3>Rechnungsempfaenger</h3>
                {(supplier.billingEmails || []).map((mail) => <p key={mail}>{mail}</p>)}
              </div>
            )}

            <div id={`preview-${group.customer}`} className="billing-preview-body">
              <BillingBlock title="Wareneingang" rows={group.rows.filter((row) => row.source === "Wareneingang").map((row) => row.text)} />
              <BillingBlock title="Warenausgang" rows={group.rows.filter((row) => row.source === "Warenausgang").map((row) => row.text)} />
              <BillingBlock title="Kartonagen" rows={(supplier.cartonPrices || []).map((row) => `${row.label || "-"} | ${row.wallType || ""} | ${row.dimensions || ""} | ${money(row.price)}`)} />
              <BillingBlock title="Porto" rows={Object.entries(supplier.shippingPrices || {}).flatMap(([provider, rows]) => (rows || []).map((row) => `${provider} | ${row.label || "-"} | ${row.zone || ""} | ${row.weightUntil || ""} kg | ${money(row.price)}`))} />
              <BillingBlock title="Mehraufwand" rows={group.rows.filter((row) => row.source === "Mehraufwand").map((row) => row.text)} />
              <BillingBlock title="Lagerplatz" rows={[]} />
              <div className="billing-block">
                <h3>Freitext Positionen</h3>
                <div className="compact-table">
                  <div className="compact-row billing-line-row compact-head"><span>Nr</span><span>Beschreibung</span><span>Anzahl</span><span>Art</span><span>Einzelpreis</span><span>Gesamt</span><span></span></div>
                  {getLines(group.customer).map((line, index) => (
                    <div className="compact-row billing-line-row" key={index}>
                      <input value={line.nr} onChange={(event) => setLine(group.customer, index, "nr", event.target.value)} />
                      <input value={line.description} onChange={(event) => setLine(group.customer, index, "description", event.target.value)} />
                      <input type="number" value={line.quantity} onChange={(event) => setLine(group.customer, index, "quantity", event.target.value)} />
                      <input value={line.unit} onChange={(event) => setLine(group.customer, index, "unit", event.target.value)} />
                      <input type="number" step="0.01" value={line.unitPrice} onChange={(event) => setLine(group.customer, index, "unitPrice", event.target.value)} />
                      <strong>{money(lineTotal(line))}</strong>
                      <div className="inline-actions">
                        <button type="button" className="button button-secondary" onClick={() => setManualLines((current) => ({ ...current, [group.customer]: [...getLines(group.customer), { ...emptyManualLine }] }))}>+</button>
                        <button type="button" className="button button-inline-danger" onClick={() => removeLine(group.customer, index)}>-</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="billing-actions">
              <div className="billing-preview-label">Vorschau</div>
              <button type="button" className="button button-primary" onClick={() => saveInvoice(group, "saved")}>Speichern</button>
              <button type="button" className="button button-secondary" onClick={() => saveInvoice(group, "printed")}>Drucken</button>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function BillingBlock({ title, rows }) {
  return (
    <div className="billing-block">
      <h3>{title}</h3>
      {rows.length ? rows.map((row, index) => <p key={index}>{row}</p>) : <p className="section-text">Keine Daten hinterlegt.</p>}
    </div>
  );
}

function FilePopup({ file, onClose }) {
  return (
    <div className="billing-popup">
      <button type="button" className="modal-close" onClick={onClose}>x</button>
      <h3>{file?.label || "Datei"}</h3>
      {file?.data ? (
        <iframe title={file.fileName || "Datei"} src={file.data} className="billing-file-frame" />
      ) : (
        <p className="section-text">Keine Datei hinterlegt.</p>
      )}
    </div>
  );
}
